import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import type {
  Invoice,
  InvoiceDirection,
  InvoiceStatus,
  UpdateKbInvoiceInput,
} from '../../../lib/invoicesApi';
import {
  useUpdateInvoiceCompany,
  useUpdateInvoiceService,
  useUpdateKbInvoice,
  useUpdateInvoiceProject,
} from '../../../hooks/useInvoices';
import { useCompanies } from '../../../hooks/useCompanies';
import { useProjects } from '../../../hooks/useProjects';
import { useServices } from '../../../hooks/useServices';
import { Select } from '../../ui/Select';

// Status labels are direction-aware: income invoices are issued/received, outcome
// invoices are to pay/paid. The shared enum (DRAFT/SENT/PAID/OVERDUE/
// CANCELLED) still backs every option so existing data keeps its meaning.
const STATUS_LABELS: Record<InvoiceDirection, Partial<Record<InvoiceStatus, string>>> = {
  INCOME: {
    DRAFT: 'Ciornă',
    SENT: 'Emisă',
    PAID: 'Încasată',
    OVERDUE: 'Întârziată',
    CANCELLED: 'Anulată',
  },
  OUTCOME: {
    DRAFT: 'Ciornă',
    SENT: 'De plătit',
    PAID: 'Plătită',
    OVERDUE: 'Întârziată',
    CANCELLED: 'Anulată',
  },
};

// Statuses surfaced for each direction, in a sensible order. DRAFT/OVERDUE/
// CANCELLED remain selectable so an invoice already in one of those states can
// be kept or corrected.
const STATUS_OPTIONS: InvoiceStatus[] = ['DRAFT', 'SENT', 'PAID', 'OVERDUE', 'CANCELLED'];

function statusLabel(direction: InvoiceDirection, status: InvoiceStatus): string {
  return STATUS_LABELS[direction][status] ?? status;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('ro-RO') : '—';
}

function formatMoney(amount: number, currency: string): string {
  return `${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/**
 * Invoice detail + edit modal (CLE-77).
 *
 * Read-only context (number, dates, amount, party, file) plus a curated edit
 * form for type / project / status. Saving drives `PATCH /api/kb/invoices/id/:id`
 * which rewrites the per-invoice markdown FIRST and re-derives the read-only DB
 * (markdown-first, CLE-41). Other fields still go through the AI assistant.
 *
 * Dismissible via the ✕ button, Escape, or a click on the backdrop.
 */
export function InvoiceDetailModal({
  invoice,
  onClose,
}: {
  invoice: Invoice;
  onClose: () => void;
}) {
  const mutation = useUpdateKbInvoice(invoice.id);
  // The service tag is DB-only metadata with its own PATCH endpoint (services
  // nomenclature) — fired alongside the KB mutation only when it changed.
  const projectMutation = useUpdateInvoiceProject(invoice.id);
  const serviceMutation = useUpdateInvoiceService(invoice.id);
  const { data: services } = useServices();
  // Multi-company: DB-only firm tag with its own PATCH endpoint. The select is
  // hidden on single-firm installs (nothing to choose).
  const companyMutation = useUpdateInvoiceCompany(invoice.id);
  const { data: companies } = useCompanies();
  const multiFirm = (companies?.length ?? 0) > 1;

  // Projects for the association dropdown (+ a "no project" option). The
  // backend caps `limit` at 100 (projects.validation.ts), so requesting more
  // (e.g. 1000) fails validation and returns NO projects — leaving the dropdown
  // showing only "no project" (CLE-78). Use the max allowed and sort by name.
  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });
  const projects = projectsData?.projects ?? [];

  const [direction, setDirection] = useState<InvoiceDirection>(invoice.direction);
  const [status, setStatus] = useState<InvoiceStatus>(invoice.status);
  // Empty string === "no project" (company expense).
  const [projectId, setProjectId] = useState<string>(invoice.project?.id ?? '');
  // Empty string === no service tag.
  const [serviceId, setServiceId] = useState<string>(invoice.service?.id ?? '');
  // Empty string === default firm (NULL companyId on the backend).
  const [companyId, setCompanyId] = useState<string>(invoice.companyId ?? '');

  // Close on Escape; lock background scroll while open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const party = invoice.direction === 'INCOME' ? invoice.client : invoice.vendor;
  const partyLabel = direction === 'INCOME' ? 'Client' : 'Furnizor';

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Send only the fields that actually changed.
    const payload: UpdateKbInvoiceInput = {};
    if (direction !== invoice.direction) payload.direction = direction;
    if (status !== invoice.status) payload.status = status;
    // The project goes through its own endpoint (PATCH /api/invoices/:id/project),
    // which works for every source — the KB route is keyed by invoice number and
    // does not reach SPV-mirrored rows.
    const nextProjectId = projectId === '' ? null : projectId;
    const projectChanged = nextProjectId !== (invoice.project?.id ?? null);

    const nextServiceId = serviceId === '' ? null : serviceId;
    const serviceChanged = nextServiceId !== (invoice.service?.id ?? null);

    const nextCompanyId = companyId === '' ? null : companyId;
    const companyChanged = nextCompanyId !== invoice.companyId;

    if (
      Object.keys(payload).length === 0 &&
      !serviceChanged &&
      !companyChanged &&
      !projectChanged
    ) {
      onClose();
      return;
    }
    void (async () => {
      try {
        if (projectChanged) await projectMutation.mutateAsync(nextProjectId);
        if (serviceChanged) await serviceMutation.mutateAsync(nextServiceId);
        if (companyChanged) await companyMutation.mutateAsync(nextCompanyId);
        if (Object.keys(payload).length > 0) await mutation.mutateAsync(payload);
        onClose();
      } catch {
        // Errors surface through the mutations' state below.
      }
    })();
  }

  const saving =
    mutation.isPending ||
    projectMutation.isPending ||
    serviceMutation.isPending ||
    companyMutation.isPending;
  const mutationError =
    mutation.error ?? projectMutation.error ?? serviceMutation.error ?? companyMutation.error;
  const serverError =
    mutationError instanceof ApiError
      ? mutationError.message
      : mutationError
        ? 'Salvarea modificărilor a eșuat.'
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Detalii factură"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Factura {invoice.invoiceNumber}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        {/* Read-only context */}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-b border-gray-200 px-5 py-4 text-sm">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Număr</dt>
            <dd className="mt-0.5 text-gray-900">{invoice.invoiceNumber}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Data emiterii
            </dt>
            <dd className="mt-0.5 text-gray-900">{formatDate(invoice.issueDate)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Scadență</dt>
            <dd className="mt-0.5 text-gray-900">{formatDate(invoice.dueDate)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Sumă</dt>
            <dd className="mt-0.5 text-gray-900">
              {formatMoney(invoice.amountTotal, invoice.currency)}
              <span className="ml-1 text-xs text-gray-500">
                (net {formatMoney(invoice.amount, invoice.currency)} + VAT{' '}
                {formatMoney(invoice.taxAmount, invoice.currency)})
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {partyLabel}
            </dt>
            <dd className="mt-0.5 text-gray-900">{party ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Plătită</dt>
            <dd className="mt-0.5 text-gray-900">{formatDate(invoice.paidAt)}</dd>
          </div>
          {invoice.s3Url && (
            <div className="col-span-2">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Fișier</dt>
              <dd className="mt-0.5">
                <a
                  href={invoice.s3Url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {invoice.fileName ?? 'Descarcă'}
                </a>
              </dd>
            </div>
          )}
        </dl>

        {/* Editable fields */}
        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">Tip</span>
            <Select
              label="Tip"
              hideLabel
              value={direction}
              onChange={(v) => setDirection(v as InvoiceDirection)}
              options={[
                { value: 'INCOME', label: 'Încasare (venit)' },
                { value: 'OUTCOME', label: 'Cheltuială (cost)' },
              ]}
              className="mt-1"
            />
          </div>

          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Proiect
            </span>
            <Select
              label="Proiect"
              hideLabel
              clearable
              searchable
              value={projectId}
              onChange={setProjectId}
              placeholder="Fără proiect (cheltuială companie)"
              options={projects.map((p) => ({
                value: p.id,
                label: p.code ? `${p.code} — ${p.name}` : p.name,
              }))}
              className="mt-1"
            />
          </div>

          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Serviciu
            </span>
            <Select
              label="Serviciu"
              hideLabel
              clearable
              value={serviceId}
              onChange={setServiceId}
              placeholder="Fără serviciu"
              options={(services ?? []).map((s) => ({
                value: s.id,
                label: s.name,
                color: s.color ?? undefined,
              }))}
              className="mt-1"
            />
          </div>

          {multiFirm && companies && (
            <div className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Firmă
              </span>
              <Select
                label="Firmă"
                hideLabel
                clearable
                value={companyId}
                onChange={setCompanyId}
                placeholder="Firma implicită"
                options={companies.map((c) => ({
                  value: c.id,
                  label: c.displayName || c.legalName,
                }))}
                className="mt-1"
              />
            </div>
          )}

          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </span>
            <Select
              label="Status"
              hideLabel
              value={status}
              onChange={(v) => setStatus(v as InvoiceStatus)}
              options={STATUS_OPTIONS.map((s) => ({ value: s, label: statusLabel(direction, s) }))}
              className="mt-1"
            />
          </div>

          {serverError && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{serverError}</p>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {saving && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {saving ? 'Se salvează…' : 'Salvează modificările'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
